import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { apiClient } from '../services/api';
import { getCustomerId, getUserAttribute } from '../utils/authUser';
import { getMarketplaceEligibilityError } from '../utils/marketplaceEligibility';

const MONTHLY_HOURS_ESTIMATE = 730;

const TIER_INFO = {
  developer: { cpu: '0.5 vCPU', memory: '1 GB', hourlyRate: 0.05 },
  starter: { cpu: '1 vCPU', memory: '2 GB', hourlyRate: 0.1375 },
  professional: { cpu: '2 vCPU', memory: '4 GB', hourlyRate: 0.275 },
  business: { cpu: '4 vCPU', memory: '8 GB', hourlyRate: 0.55 },
  enterprise: { cpu: '8 vCPU', memory: '16 GB', hourlyRate: 1.1 }
};

const formatMonthlyEstimate = (hourlyRate) => `$${Math.round(hourlyRate * MONTHLY_HOURS_ESTIMATE)}/mo`;

const REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' }
];

const EDITIONS = [
  {
    value: 'opensource',
    label: 'Virtuoso Open Source Edition',
    description: 'Community edition with core RDF/Graph and SQL features',
    badge: 'Free'
  },
  {
    value: 'commercial',
    label: 'Virtuoso Commercial Edition',
    description: 'Enterprise features: Cluster, Column Store, Advanced Security, Full-Text Search',
    badge: 'Enterprise'
  }
];
const TERMINAL_INSTANCE_STATUSES = new Set(['terminated', 'failed']);

const isNonTerminalInstance = (instance) => {
  const status = (instance?.status || '').toLowerCase();
  return !TERMINAL_INSTANCE_STATUSES.has(status);
};

function NewInstance() {
  const { user, activeCustomerId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    projectName: '',
    tier: 'starter',
    region: 'us-east-1',
    edition: 'opensource',
    sqlAllowedCidrs: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingBlockingInstance, setExistingBlockingInstance] = useState(null);
  const [checkingInstanceLimit, setCheckingInstanceLimit] = useState(true);
  const [marketplaceEligibilityError, setMarketplaceEligibilityError] = useState('');

  useEffect(() => {
    const customerId = activeCustomerId || getCustomerId(user);
    if (!customerId) {
      setExistingBlockingInstance(null);
      setMarketplaceEligibilityError('Customer ID not found. Please contact support.');
      setCheckingInstanceLimit(false);
      return undefined;
    }

    let isMounted = true;
    setCheckingInstanceLimit(true);
    setMarketplaceEligibilityError('');

    const applyTierIfValid = (value) => {
      const normalizedTier = (value || '').toLowerCase();
      if (TIER_INFO[normalizedTier]) {
        setFormData(prev => ({ ...prev, tier: normalizedTier }));
      }
    };

    const prefillTierFromCustomer = async () => {
      try {
        const [customerResponse, instancesResponse] = await Promise.all([
          apiClient.get(`/customers/${customerId}`),
          apiClient.get(`/instances?customer_id=${customerId}`)
        ]);
        if (!isMounted) {
          return;
        }
        applyTierIfValid(customerResponse.data?.tier);
        setMarketplaceEligibilityError(getMarketplaceEligibilityError(customerResponse.data));
        const blocking = (instancesResponse.data?.instances || []).find(isNonTerminalInstance) || null;
        setExistingBlockingInstance(blocking);
      } catch (prefillError) {
        if (!isMounted) {
          return;
        }
        applyTierIfValid(getUserAttribute(user, 'custom:tier'));
        setExistingBlockingInstance(null);
        setMarketplaceEligibilityError('Unable to validate AWS Marketplace agreement for this customer.');
      } finally {
        if (isMounted) {
          setCheckingInstanceLimit(false);
        }
      }
    };

    prefillTierFromCustomer();

    return () => {
      isMounted = false;
    };
  }, [user, activeCustomerId]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const effectiveCustomerId = activeCustomerId || getCustomerId(user);
      if (!effectiveCustomerId) {
        throw new Error('Customer ID not found. Please contact support.');
      }
      if (existingBlockingInstance) {
        throw new Error('Single-instance subscription limit reached. Terminate your existing instance before creating another.');
      }
      if (marketplaceEligibilityError) {
        throw new Error(marketplaceEligibilityError);
      }

      const response = await apiClient.post('/instances', {
        customer_id: effectiveCustomerId,
        project_name: formData.projectName,
        tier: formData.tier,
        region: formData.region,
        virtuoso_edition: formData.edition,
        sql_allowed_cidrs: formData.sqlAllowedCidrs
          .split(',')
          .map(cidr => cidr.trim())
          .filter(Boolean)
      });

      navigate(`/instances/${response.data.instance_id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create instance');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/instances" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '14px' }}>
            ← Back to Instances
          </Link>
          <h1 style={{ marginTop: '8px' }}>Create New Instance</h1>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '24px' }}>
        {/* Form */}
        <div className="card">
          {existingBlockingInstance && (
            <div style={{
              background: 'rgba(237, 137, 54, 0.12)',
              border: '1px solid var(--warning-color)',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              marginBottom: '20px',
              color: 'var(--text-color)'
            }}>
              Single-instance plan limit reached. You already have instance{' '}
              <Link to={`/instances/${existingBlockingInstance.instance_id}`} style={{ color: 'var(--primary-color)' }}>
                {existingBlockingInstance.project_name || existingBlockingInstance.instance_id}
              </Link>{' '}
              in status <strong>{existingBlockingInstance.status}</strong>. Terminate it before creating another.
            </div>
          )}

          {!existingBlockingInstance && marketplaceEligibilityError && (
            <div style={{
              background: 'rgba(237, 137, 54, 0.12)',
              border: '1px solid var(--warning-color)',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              marginBottom: '20px',
              color: 'var(--text-color)'
            }}>
              {marketplaceEligibilityError}
            </div>
          )}

          {error && (
            <div style={{
              background: 'rgba(245, 101, 101, 0.1)',
              border: '1px solid var(--danger-color)',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
              marginBottom: '20px',
              color: 'var(--danger-color)'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <fieldset
              disabled={loading || !!existingBlockingInstance || !!marketplaceEligibilityError || checkingInstanceLimit}
              style={{ border: 'none', margin: 0, padding: 0 }}
            >
            <div className="form-group">
              <label htmlFor="projectName">Instance Name</label>
              <input
                type="text"
                id="projectName"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="my-virtuoso-instance"
                pattern="[a-z0-9-]+"
                required
              />
              <p className="help-text">
                Lowercase letters, numbers, and hyphens only. This will be used as a prefix for all resources.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="tier">Instance Tier</label>
              <select
                id="tier"
                name="tier"
                value={formData.tier}
                onChange={handleChange}
                required
              >
                {Object.entries(TIER_INFO).map(([key, info]) => (
                  <option key={key} value={key}>
                    {key.charAt(0).toUpperCase() + key.slice(1)} - {info.cpu}, {info.memory} - {formatMonthlyEstimate(info.hourlyRate)}
                  </option>
                ))}
              </select>
              <p className="help-text">
                Choose based on your workload requirements. You can upgrade later.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="region">Region</label>
              <select
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                required
              >
                {REGIONS.map(region => (
                  <option key={region.value} value={region.value}>
                    {region.label}
                  </option>
                ))}
              </select>
              <p className="help-text">
                Choose a region closest to your users for best performance.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="sqlAllowedCidrs">Secure SQL Allowed CIDRs (optional)</label>
              <input
                type="text"
                id="sqlAllowedCidrs"
                name="sqlAllowedCidrs"
                value={formData.sqlAllowedCidrs}
                onChange={handleChange}
                placeholder="203.0.113.45/32, 198.51.100.0/24"
              />
              <p className="help-text">
                Comma-separated trusted IPv4 ranges for TLS SQL on port 1112. Leave blank to disable public SQL access; Container Shell Access remains available. `0.0.0.0/0` is not allowed.
              </p>
            </div>

            <div className="form-group">
              <label>Virtuoso Edition</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                {EDITIONS.map(edition => (
                  <label
                    key={edition.value}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '16px',
                      border: `2px solid ${formData.edition === edition.value ? 'var(--primary-color)' : 'var(--border-color)'}`,
                      borderRadius: 'var(--radius)',
                      cursor: 'pointer',
                      background: formData.edition === edition.value ? 'rgba(79, 70, 229, 0.05)' : 'transparent',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="radio"
                      name="edition"
                      value={edition.value}
                      checked={formData.edition === edition.value}
                      onChange={handleChange}
                      style={{ marginRight: '12px', marginTop: '4px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 600 }}>{edition.label}</span>
                        <span style={{
                          background: edition.value === 'commercial' ? 'var(--primary-color)' : 'var(--success-color)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          {edition.badge}
                        </span>
                      </div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>
                        {edition.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '32px' }}>
              <button type="submit" className="btn btn-primary" disabled={loading || !!existingBlockingInstance || !!marketplaceEligibilityError || checkingInstanceLimit}>
                {checkingInstanceLimit
                  ? 'Checking Instance Limit...'
                  : loading
                    ? 'Creating Instance...'
                    : existingBlockingInstance
                      ? 'Single Instance Limit Reached'
                      : marketplaceEligibilityError
                        ? 'Marketplace Agreement Required'
                        : 'Create Instance'}
              </button>
            </div>
            </fieldset>
          </form>
        </div>

        {/* Summary */}
        <div>
          <div className="card">
            <h3 style={{ marginBottom: '16px' }}>Instance Summary</h3>

            <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Instance Name</p>
              <p style={{ fontWeight: 600 }}>{formData.projectName || 'Not set'}</p>
            </div>

            <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Tier</p>
              <p style={{ fontWeight: 600, textTransform: 'capitalize' }}>{formData.tier}</p>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                {TIER_INFO[formData.tier].cpu} • {TIER_INFO[formData.tier].memory}
              </p>
            </div>

            <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Region</p>
              <p style={{ fontWeight: 600 }}>
                {REGIONS.find(r => r.value === formData.region)?.label}
              </p>
            </div>

            <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Edition</p>
              <p style={{ fontWeight: 600 }}>
                {EDITIONS.find(e => e.value === formData.edition)?.label}
              </p>
              <span style={{
                display: 'inline-block',
                background: formData.edition === 'commercial' ? 'var(--primary-color)' : 'var(--success-color)',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 600,
                marginTop: '4px'
              }}>
                {EDITIONS.find(e => e.value === formData.edition)?.badge}
              </span>
            </div>

            <div>
              <p style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Estimated Cost</p>
              <p style={{ fontWeight: 700, fontSize: '24px', color: 'var(--primary-color)' }}>
                {formatMonthlyEstimate(TIER_INFO[formData.tier].hourlyRate)}
              </p>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Estimated from hourly metering at {MONTHLY_HOURS_ESTIMATE}h/month
              </p>
            </div>
          </div>

          <div className="card" style={{ marginTop: '16px' }}>
            <h4 style={{ marginBottom: '12px' }}>What's Included</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: 'var(--success-color)', marginRight: '8px' }}>✓</span>
                Virtuoso Universal Server
              </li>
              <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: 'var(--success-color)', marginRight: '8px' }}>✓</span>
                Automatic backups
              </li>
              <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: 'var(--success-color)', marginRight: '8px' }}>✓</span>
                HTTPS endpoints
              </li>
              <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: 'var(--success-color)', marginRight: '8px' }}>✓</span>
                24/7 monitoring
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewInstance;
